import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { ArcElement, Chart as ChartJS, Legend, Tooltip as ChartTooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";

import ThemeColors from "../../shared/components/ThemeColors/ThemeColors";
import { ReactTyped } from "react-typed";
import { useSidebarDarkMode } from "../../layouts/private/components/sidebar/sidebarColorMode";

import useSentimentsAI from "./hooks/useSentimentsAI";

ChartJS.register(ArcElement, ChartTooltip, Legend);

const Comprehend = ({ sentiments, totalMessages, loading }) => {
  const sidebarDark = useSidebarDarkMode();
  const defaultSentiments = {
    Positive: 0,
    Negative: 0,
    Neutral: 0,
    Mixed: 0,
  };

  const mergedSentiments = { ...defaultSentiments, ...sentiments };
  const { aiAnalysisText, aiAnalysisLoading, generateAnalysis } =
    useSentimentsAI();

  const [typeView, setTypeView] = useState("analysis");
  const [dots, setDots] = useState("");

  const sentimentItems = [
    {
      key: "Positive",
      label: "Positivo",
      color: sidebarDark ? "#12833A" : "#16A34A",
      hoverColor: sidebarDark ? "#116B33" : "#15803D",
      description: "Mensagens com intenção favorável, aprovação ou interesse.",
      value: Number(mergedSentiments.Positive) || 0,
    },
    {
      key: "Neutral",
      label: "Neutro",
      color: sidebarDark ? ThemeColors.purple600 : ThemeColors.purple500,
      hoverColor: sidebarDark ? ThemeColors.purple700 : ThemeColors.purple600,
      description: "Mensagens sem tom claramente positivo ou negativo.",
      value: Number(mergedSentiments.Neutral) || 0,
    },
    {
      key: "Negative",
      label: "Negativo",
      color: sidebarDark ? "#BE123C" : "#E11D48",
      hoverColor: sidebarDark ? "#9F1239" : "#BE123C",
      description: "Mensagens com reclamações, frustração ou rejeição.",
      value: Number(mergedSentiments.Negative) || 0,
    },
    {
      key: "Mixed",
      label: "Misto",
      color: sidebarDark ? "#475569" : "#64748B",
      hoverColor: sidebarDark ? "#334155" : "#475569",
      description: "Mensagens com sinais combinados ou interpretação indefinida.",
      value: Number(mergedSentiments.Mixed) || 0,
    },
  ];

  const totalSentimentPercent = sentimentItems.reduce(
    (acc, item) => acc + Math.max(item.value, 0),
    0
  );

  const hasSentimentData = totalSentimentPercent > 0;
  const chartLabels = hasSentimentData
    ? sentimentItems.map((item) => item.label)
    : ["Sem dados"];
  const chartDataValues = hasSentimentData
    ? sentimentItems.map((item) => Math.max(item.value, 0))
    : [1];
  const chartColors = hasSentimentData
    ? sentimentItems.map((item) => item.color)
    : ["#E7EAF0"];
  const chartHoverColors = hasSentimentData
    ? sentimentItems.map((item) => item.hoverColor)
    : ["#DCE2EA"];

  const doughnutData = {
    labels: chartLabels,
    datasets: [
      {
        data: chartDataValues,
        backgroundColor: chartColors,
        borderColor: "#FFFFFF",
        borderRadius: hasSentimentData ? 4 : 0,
        borderWidth: hasSentimentData ? 4 : 0,
        hoverBackgroundColor: chartHoverColors,
        hoverBorderColor: "#FFFFFF",
        hoverBorderWidth: hasSentimentData ? 4 : 0,
        hoverOffset: hasSentimentData ? 5 : 0,
        spacing: hasSentimentData ? 2 : 0,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "67%",
    animation: {
      duration: 450,
      easing: "easeOutQuart",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#FFFFFF",
        titleColor: "#253145",
        bodyColor: "#64748B",
        borderColor: "#E3E8F0",
        borderWidth: 1,
        displayColors: true,
        padding: 10,
        cornerRadius: 8,
        boxPadding: 5,
        callbacks: {
          label: (context) => {
            if (!hasSentimentData) return "Sem mensagens analisadas";

            const value = Number(context.parsed) || 0;
            return `${context.label}: ${value.toFixed(2)}%`;
          },
        },
      },
    },
  };

  const centerTextPlugin = {
    id: "sentimentCenterText",
    afterDraw: (chart) => {
      const meta = chart.getDatasetMeta(0);
      const arc = meta?.data?.[0];
      if (!arc) return;

      const { ctx } = chart;
      const centerX = arc.x;
      const centerY = arc.y;

      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#8A93A4";
      ctx.font = "600 11px Inter, Roboto, Arial, sans-serif";
      ctx.fillText("Interações", centerX, centerY - 14);
      ctx.fillStyle = "#4B5563";
      ctx.font = "600 24px Inter, Roboto, Arial, sans-serif";
      ctx.fillText(String(totalMessages || 0), centerX, centerY + 10);
      ctx.restore();
    },
  };

  const handleGenerateAnalysis = () => {
    const data = {
      sentiments: mergedSentiments,
      totalMessages,
    };

    setTypeView("analysis_ai");
    generateAnalysis(data);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length === 3 ? "" : prev + "."));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      flex={1}
      borderRadius="12px"
      padding={{ xs: "18px", sm: "22px", xl: "24px" }}
      sx={{
        background: "#FFFFFF",
        border: sidebarDark ? "1px solid #D6CCDF" : "1px solid #D9DEE8",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        height: { xs: "auto", lg: "292px", xl: "300px" },
        minHeight: { xs: "auto", lg: "292px", xl: "300px" },
        boxShadow: "0 14px 30px rgba(15, 23, 42, 0.075)",
      }}
    >
      <Box
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: "12px", sm: "16px" },
          justifyContent: "space-between",
          mb: { xs: "12px", sm: "14px" },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: sidebarDark ? "#281543" : "#2F194F",
              fontWeight: 500,
              fontSize: { xl: "21px", xs: "19px" },
              lineHeight: 1.15,
            }}
          >
            Análise Geral de Sentimentos
            <Typography
              component="span"
              sx={{
                color: sidebarDark ? "#5E5478" : "#6A5F87",
                fontSize: { xs: "13px", xl: "14px" },
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {" "}
              ( Clientes )
            </Typography>
          </Typography>

          <Typography
            sx={{
              color: sidebarDark ? "#687386" : "#7A8495",
              fontSize: { xs: "11px", xl: "12px" },
              fontWeight: 500,
              lineHeight: 1.4,
              mt: "6px",
            }}
          >
            Distribuição das interações analisadas por intenção
          </Typography>
        </Box>

        <Button
          variant="contained"
          endIcon={<AutoAwesomeIcon sx={{ color: ThemeColors.purple700 }} />}
          onClick={
            typeView === "analysis"
              ? handleGenerateAnalysis
              : () => setTypeView("analysis")
          }
          sx={{
            alignSelf: { xs: "stretch", sm: "flex-start" },
            background: "#F4EEFF",
            border: "1px solid #E0D0FF",
            borderRadius: "10px",
            boxShadow: "none",
            color: ThemeColors.purple700,
            flexShrink: 0,
            fontSize: { xs: "11px", xl: "13px" },
            fontWeight: 600,
            minHeight: { xs: "36px", sm: "34px" },
            px: 1.5,
            textTransform: "none",
            "&:hover": {
              background: "#EEE4FF",
              borderColor: "#CFB7FF",
              boxShadow: "none",
            },
          }}
        >
          {typeView === "analysis" ? "Análise com IA" : "Voltar aos dados"}
        </Button>
      </Box>

      <Box
        display="flex"
        sx={{
          alignItems: "center",
          flex: 1,
          flexDirection: "column",
          gap: { xs: "18px", sm: "16px" },
          justifyContent: "center",
          minHeight: 0,
          py: 0,
          width: "100%",
        }}
      >
        {!loading && typeView === "analysis" && (
          <Box
            sx={{
              alignItems: "center",
              background: sidebarDark
                ? "linear-gradient(180deg, #FBFAFD 0%, #F6F8FB 100%)"
                : "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
              border: sidebarDark ? "1px solid #E6DFF0" : "1px solid #EDF1F6",
              borderRadius: "12px",
              display: "grid",
              gap: { xs: "18px", sm: "28px", xl: "36px" },
              gridTemplateColumns: { xs: "1fr", sm: "auto minmax(188px, 230px)" },
              justifyContent: "center",
              minHeight: { xs: "auto", sm: "182px", xl: "192px" },
              mx: "auto",
              px: { xs: "16px", sm: "20px", xl: "24px" },
              py: { xs: "18px", sm: "12px", xl: "14px" },
              width: "100%",
            }}
          >
            <Box
              aria-label="Distribuição de sentimentos"
              sx={{
                height: { xs: "144px", sm: "156px", xl: "166px" },
                justifySelf: "center",
                position: "relative",
                width: { xs: "144px", sm: "156px", xl: "166px" },
              }}
            >
              <Doughnut
                data={doughnutData}
                options={doughnutOptions}
                plugins={[centerTextPlugin]}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: { xs: "10px", sm: "11px" },
                gridTemplateColumns: "1fr",
                width: "100%",
              }}
            >
              {sentimentItems.map((item) => (
                <Box
                  key={item.key}
                  sx={{
                    alignItems: "center",
                    display: "grid",
                    gap: "8px",
                    gridTemplateColumns: "auto minmax(0, 1fr) auto",
                    minWidth: 0,
                  }}
                >
                  <Tooltip title={item.description} arrow placement="top">
                    <HelpOutlineIcon
                      sx={{
                        color: item.color,
                        cursor: "pointer",
                        fontSize: { xs: "15px", xl: "16px" },
                      }}
                    />
                  </Tooltip>

                  <Typography
                    sx={{
                      color: sidebarDark ? "#334155" : "#475569",
                      fontSize: { xs: "12px", xl: "13px" },
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    sx={{
                      color: sidebarDark ? "#687386" : "#8A93A4",
                      fontSize: { xs: "12px", xl: "13px" },
                      fontWeight: 600,
                      justifySelf: "end",
                    }}
                  >
                    {item.value.toFixed(2)}%
                  </Typography>
                </Box>
              ))}

            </Box>
          </Box>
        )}

        {!aiAnalysisLoading && typeView === "analysis_ai" && (
          <Typography
            sx={{
              color: "#2D2A39",
              fontSize: { xs: "14px", xl: "15px" },
              lineHeight: "1.5",
              maxHeight: { xs: "180px", sm: "150px" },
              overflowY: "auto",
              whiteSpace: "pre-line",
              width: "100%",
            }}
          >
            <ReactTyped
              strings={[aiAnalysisText]}
              typeSpeed={10}
              showCursor={false}
            />
          </Typography>
        )}

        {aiAnalysisLoading && typeView === "analysis_ai" && (
          <Box display="flex" alignItems="center">
            <AutoAwesomeIcon
              sx={{ color: ThemeColors.purple600, marginRight: "10px", fontSize: "20px" }}
            />
            <Typography sx={{ color: "#463A5D", fontWeight: 500 }}>
              Aguarde, estou gerando a análise{dots}
            </Typography>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              alignItems: "center",
              display: "grid",
              gap: { xs: "20px", sm: "34px", xl: "42px" },
              gridTemplateColumns: { xs: "1fr", sm: "auto minmax(196px, 236px)" },
              justifyContent: "center",
              maxWidth: { xs: "100%", sm: "560px", xl: "600px" },
              mx: "auto",
              width: "100%",
            }}
          >
            <Box
              sx={{
                "@keyframes sentimentPulse": {
                  "0%": { opacity: 0.45 },
                  "50%": { opacity: 0.85 },
                  "100%": { opacity: 0.45 },
                },
                animation: "sentimentPulse 1.6s ease-in-out infinite",
                background:
                  "conic-gradient(#E6EAF2 0deg 110deg, #F1F4F8 110deg 240deg, #E6EAF2 240deg 360deg)",
                borderRadius: "50%",
                height: { xs: "144px", sm: "156px", xl: "166px" },
                justifySelf: "center",
                position: "relative",
                width: { xs: "144px", sm: "156px", xl: "166px" },
                "&::after": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "50%",
                  content: '""',
                  height: "67%",
                  left: "50%",
                  position: "absolute",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "67%",
                },
              }}
            />
            <Box
              sx={{
                display: "grid",
                gap: { xs: "10px", sm: "11px" },
                width: "100%",
              }}
            >
              {[0, 1, 2, 3].map((item) => (
                <Box
                  key={item}
                  sx={{
                    alignItems: "center",
                    animation: "sentimentPulse 1.6s ease-in-out infinite",
                    display: "grid",
                    gap: "8px",
                    gridTemplateColumns: "16px minmax(0, 1fr) 54px",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#E6EAF2",
                      borderRadius: "50%",
                      height: "15px",
                      width: "15px",
                    }}
                  />
                  <Box
                    sx={{
                      backgroundColor: "#EEF2F7",
                      borderRadius: "999px",
                      height: "13px",
                      width: item % 2 === 0 ? "76%" : "58%",
                    }}
                  />
                  <Box
                    sx={{
                      backgroundColor: "#EEF2F7",
                      borderRadius: "999px",
                      height: "13px",
                      justifySelf: "end",
                      width: item % 2 === 0 ? "48px" : "40px",
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Comprehend;
